import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, ButtonGroup, Flex, FormLabel, Input, Spinner, Stack } from '@chakra-ui/react';

import { UserState } from 'services/user';
import { debounceHandler, useFetchOnMount } from 'utils';

import { Title } from 'scenes/index.styled';

const groupByWorkPlace = (users) =>
  users.reduce((acc, user) => {
    const key = user.workPlace || 'bez-poslovnice';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(user);
    return acc;
  }, {});

const normalizeGroupedOrder = (users) => {
  const grouped = groupByWorkPlace(users);
  const next = {};

  Object.keys(grouped).forEach((workPlace) => {
    grouped[workPlace].forEach((user, index) => {
      next[user.id] = String(index + 1);
    });
  });

  return next;
};

const Poredak = () => {
  const { loading, users, fetchUsers, setReportSortOrder } = useContext(UserState);
  const [query, setQuery] = useState('');
  const [draftOrders, setDraftOrders] = useState({});

  useFetchOnMount(() => {
    fetchUsers('report');
  });

  useEffect(() => {
    if (users) {
      const next = {};
      users.forEach((user) => {
        next[user.id] =
          user.reportSortOrder === null || typeof user.reportSortOrder === 'undefined'
            ? ''
            : String(user.reportSortOrder);
      });
      setDraftOrders(next);
    }
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (!users) {
      return [];
    }

    return users.filter((user) => {
      const fullName = `${user.firstname} ${user.lastname}`.toLowerCase();
      const username = user.username.toLowerCase();
      const workPlace = (user.workPlace || '').toLowerCase();
      const search = query.toLowerCase();

      return fullName.includes(search) || username.includes(search) || workPlace.includes(search);
    });
  }, [query, users]);

  const groupedUsers = useMemo(() => groupByWorkPlace(filteredUsers), [filteredUsers]);

  const handleOrderChange = (userId, value) => {
    setDraftOrders((current) => ({
      ...current,
      [userId]: value.replace(/[^0-9]/g, ''),
    }));
  };

  const handleNormalize = () => {
    if (!users) {
      return;
    }

    setDraftOrders(normalizeGroupedOrder(users));
  };

  const handleSave = async () => {
    if (!users) {
      return;
    }

    const updates = users.map((user) => {
      const rawValue = draftOrders[user.id];

      return {
        id: user.id,
        reportSortOrder:
          rawValue === '' || typeof rawValue === 'undefined' ? null : Number(rawValue),
      };
    });

    await setReportSortOrder(updates);
    await fetchUsers('report');
  };

  return (
    <Box paddingTop="12px">
      <Flex justifyContent="space-between" marginBottom="24px" alignItems="center">
        <Title>Poredak korisnika za izvještaje</Title>
        <ButtonGroup size="sm">
          <Button variant="outline" colorScheme="pink" onClick={handleNormalize}>
            Normaliziraj
          </Button>
          <Button colorScheme="pink" onClick={handleSave} isLoading={loading}>
            Spremi
          </Button>
        </ButtonGroup>
      </Flex>

      <Flex justifyContent="space-between" marginBottom="16px" alignItems="flex-end">
        <Box width="280px">
          <FormLabel fontSize="sm" marginBottom="6px">
            Traži korisnika
          </FormLabel>
          <Input
            size="sm"
            placeholder="Ime, prezime, korisničko ime"
            onChange={debounceHandler((e) => setQuery(e.target.value))}
          />
        </Box>
      </Flex>

      {loading && !users ? (
        <Spinner color="pink.500" />
      ) : (
        <Stack spacing={6}>
          {Object.entries(groupedUsers).map(([workPlace, grouped]) => (
            <Box key={workPlace} borderWidth="1px" borderRadius="md" padding="16px">
              <Box fontWeight="700" marginBottom="12px">
                {workPlace}
              </Box>
              <Stack spacing={3}>
                {grouped.map((user) => (
                  <Flex key={user.id} justifyContent="space-between" alignItems="center" gap={4}>
                    <Box flex="1">
                      <Box fontWeight="600">
                        {user.firstname} {user.lastname}
                      </Box>
                      <Box fontSize="sm" color="gray.500">
                        {user.username}
                      </Box>
                    </Box>
                    <Input
                      size="sm"
                      width="120px"
                      textAlign="right"
                      placeholder="Poredak"
                      value={draftOrders[user.id] || ''}
                      onChange={(e) => handleOrderChange(user.id, e.target.value)}
                    />
                  </Flex>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default Poredak;
